import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  SquaresFour, MapTrifold, UsersThree, CalendarCheck,
  Storefront, Stamp, DownloadSimple, SignOut, Broadcast,
} from "@phosphor-icons/react";
import api from "@/lib/api";

const NAV = [
  { to: "/", label: "Dashboard", short: "Home", icon: SquaresFour, end: true },
  { to: "/map", label: "Live Map", short: "Map", icon: MapTrifold },
  { to: "/employees", label: "Employees", short: "Team", icon: UsersThree },
  { to: "/attendance", label: "Attendance", short: "Attend", icon: CalendarCheck },
  { to: "/visits", label: "Field Visits", short: "Visits", icon: Storefront },
  { to: "/approvals", label: "Approvals", short: "Approve", icon: Stamp },
  { to: "/reports", label: "Reports", short: "Reports", icon: DownloadSimple },
];

export default function Layout() {
  const navigate = useNavigate();
  const [business, setBusiness] = useState("SalesTrack Pro");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    api.get("/proxy/settings").then((r) => {
      if (r.data?.business_name) setBusiness(r.data.business_name);
    }).catch(() => {});
  }, []);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-white">
      <header className="lg:hidden fixed top-0 inset-x-0 h-14 z-40 bg-white border-b border-slate-200 flex items-center justify-between px-4" data-testid="mobile-header">
        <div className="flex items-center gap-2 min-w-0">
          <Broadcast size={20} weight="duotone" className="text-[#007AFF] shrink-0" />
          <div className="min-w-0">
            <span className="font-heading font-extrabold tracking-tight text-sm block leading-tight">SalesTrack Pro</span>
            <p className="text-[9px] text-slate-500 uppercase tracking-[0.15em] truncate">{business}</p>
          </div>
        </div>
        <button onClick={logout} data-testid="mobile-logout-btn" className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-[#FF3B30] transition-colors duration-150">
          <SignOut size={16} /> Sign out
        </button>
      </header>

      <aside className="w-60 shrink-0 border-r border-slate-200 hidden lg:flex flex-col fixed inset-y-0" data-testid="sidebar">
        <div className="px-5 py-6 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Broadcast size={22} weight="duotone" className="text-[#007AFF]" />
            <span className="font-heading font-extrabold tracking-tight text-lg">SalesTrack Pro</span>
          </div>
          <p className="text-xs text-slate-500 mt-1 uppercase tracking-[0.2em]">{business}</p>
        </div>
        <nav className="flex-1 py-4">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors duration-150 border-l-[3px] ${
                  isActive
                    ? "border-[#007AFF] bg-slate-50 font-semibold text-slate-950"
                    : "border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                }`
              }
            >
              <Icon size={18} weight="duotone" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 px-5 py-4">
          <p className="text-sm font-semibold" data-testid="current-user-name">{user.name}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider">{user.role} · {user.employee_id}</p>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="mt-3 flex items-center gap-2 text-xs text-slate-600 hover:text-[#FF3B30] transition-colors duration-150"
          >
            <SignOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 grid grid-cols-7 pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
        {NAV.map(({ to, short, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`mobilenav-${label.toLowerCase().replace(" ", "-")}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 py-2 transition-colors duration-150 ${
                isActive ? "text-[#007AFF]" : "text-slate-500"
              }`
            }
          >
            <Icon size={20} weight="duotone" />
            <span className="text-[9px] font-semibold leading-none">{short}</span>
          </NavLink>
        ))}
      </nav>

      <main className="flex-1 lg:ml-60 min-w-0 pt-14 lg:pt-0 pb-16 lg:pb-0">
        <Outlet />
      </main>
    </div>
  );
}
